import { ReactElement } from "react";
import { useRoute } from "_/components/router";
import { t } from "@lingui/core/macro";

import * as S from "./styled";
import { IconVariant } from "_/components/icon";
import { isOrgUrl, routeUrls, routes } from "_/routes";

import { useCheckCurrentOrg, useCurrentOrgPermissions } from "_/data/orgs";
import { testPermissions } from "_/components/permission-test";
import { type Permission } from "_/data/rbac";

/**
 * Navigation item listed under a group header, representing a complete path.
 */
type NavItem = {
  label: string;
  route: string | RegExp;
  url: string;
  icon: IconVariant;
  requiredPermissions?: Permission | Permission[] | Permission[][];
};

// Dummy route using an invalid URL characters so that a mailto link will not be
// "matched" on and styled as active.
const UNSELECTABLE_ROUTE = "<<{{ UNSELECTABLE }}>>";

const NavLink = (props: { navItem: NavItem }): ReactElement => {
  const { label, icon, route, url } = props.navItem;
  const [match, _] = useRoute(route);
  const { data: currentOrg } = useCheckCurrentOrg();

  const disabled = isOrgUrl(url) && !currentOrg;
  const active = route === UNSELECTABLE_ROUTE ? false : match;

  return (
    <S.Link to={url} $active={active} $disabled={disabled}>
      <S.Icon variant={icon} />
      <S.LabelText>{label}</S.LabelText>
    </S.Link>
  );
};

export const Sidebar = (): ReactElement => {
  const orgPerms = useCurrentOrgPermissions();

  const allNavItems: NavItem[] = [
    {
      label: t`common.home`,
      icon: "Home",
      route: routes.index,
      url: routeUrls.index,
      requiredPermissions: [["projects/read"], ["machines/read"]],
    },
    {
      label: t`common.projects`,
      icon: "Projects",
      route: routes.projects.wildcards.index,
      url: routeUrls.projects.index,
      requiredPermissions: "projects/read",
    },
    {
      label: t`common.machines`,
      icon: "Machines",
      route: routes.machines.wildcards.index,
      url: routeUrls.machines.index,
      requiredPermissions: "machines/read",
    },
    {
      label: t`common.jobs`,
      icon: "Jobs",
      route: routes.jobs,
      url: routeUrls.jobs,
    },
  ];

  const navItems = allNavItems.filter(
    (item) =>
      !item.requiredPermissions ||
      testPermissions(item.requiredPermissions, orgPerms)
  );

  const bottomNavItems: NavItem[] = [
    {
      label: t`common.docs`,
      icon: "Docs",
      route: routes.docs.wildcards.index,
      url: routeUrls.docs.index,
    },
    {
      label: t`components.sidebar.support`,
      icon: "Support",
      route: UNSELECTABLE_ROUTE,
      url: "mailto:help@aon3d.com",
    },
  ];

  return (
    <S.Wrapper>
      <S.TopNav>
        {navItems.map((navItem, i) => (
          <NavLink navItem={navItem} key={i} />
        ))}
      </S.TopNav>
      <S.BottomNav>
        {bottomNavItems.map((navItem, i) => (
          <NavLink navItem={navItem} key={i} />
        ))}
      </S.BottomNav>
    </S.Wrapper>
  );
};
